import { flattenDeep, isNull } from 'lodash/fp';

import { ApplicationState } from '../../../store/types';
import { sortPlaybookShelves } from '../../constants/playbooks';
import { PlaybookQuery } from './types';
import { BasicPlaybook, PlaybookHistory, PlaybookView, PlaybookDB, PlaybookMessage, PlaybookQueryDB, SuggestedChangesDB, ReadOnlyContentType, PlaybookSmartSearch, LinkedPlaybookProvision, SuggestedChanges } from '../../admin/playbook/store/types';
import { getAvailableDocumentNames } from '../../admin/documents/store/selectors';
import { getPlaybookRoot } from '../../../store/rootSelector';

export const getAllPlaybooks = (state: ApplicationState): BasicPlaybook[] => getPlaybookRoot(state).allPlaybooks;

export const getPlaybookShelves = (state: ApplicationState) => {
    const searchTerm = getPlaybookSimpleSearchTerm(state);
    let allPlaybooks: BasicPlaybook[] = getPlaybookRoot(state).allPlaybooks;
    if (searchTerm !== '') {
        const allDocumentNames = getAvailableDocumentNames(state);
        const getDocumentNames = (ids: string[] | null) => ids ? allDocumentNames.reduce((acc: string[], { documentNameId, documentName }) => documentNameId && ids.includes(documentNameId.toString()) ? [...acc, documentName.toLowerCase()] : acc, []) : [];
        const playbooksToSearch = allPlaybooks.map(({ playbookId, agreementType, createdBy, name, documentNameIds, versionMajor, versionMinor }) => ({ playbookId: playbookId, searchableValues: flattenDeep([!isNull(agreementType) ? agreementType.toString().toLowerCase() : '', createdBy.toLowerCase(), name.toLowerCase(), getDocumentNames(documentNameIds), versionMajor.toString(), `${versionMajor.toString()}.${versionMinor.toString()}`]) }));
        const matchedPlaybookIds = playbooksToSearch.filter(({ searchableValues }) => searchableValues.some(text => text.includes(searchTerm))).map(({ playbookId }) => playbookId);
        allPlaybooks = allPlaybooks.filter(playbook => matchedPlaybookIds.includes(playbook.playbookId));
    }
    const playbookShelves = sortPlaybookShelves(allPlaybooks);
    return playbookShelves;
};

export const getPlaybookCoverSelected = (state: ApplicationState): BasicPlaybook | null => getPlaybookRoot(state).playbookCoverSelected;

export const getPlaybookCoverModalOpen = (state: ApplicationState): boolean => !isNull(getPlaybookCoverSelected(state));

export const getUserPlaybook = (state: ApplicationState): PlaybookDB | null => getPlaybookRoot(state).playbook;

export const getUserPlaybookHistory = (state: ApplicationState): PlaybookHistory[] => getPlaybookRoot(state).playbookHistory;

export const getUserCurrentView = (state: ApplicationState): PlaybookView => getPlaybookRoot(state).currentView;

export const getQuerySectionId = (state: ApplicationState): string | null => getPlaybookRoot(state).querySectionId;

export const getQueryModalOpen = (state: ApplicationState): boolean => !isNull(getQuerySectionId(state)) || !isNull(getPlaybookQuery(state));

export const getPlaybookQuery = (state: ApplicationState): PlaybookQuery | null => getPlaybookRoot(state).query;

export const getQueryReady = (state: ApplicationState): boolean => getPlaybookRoot(state).queryReady;

export const getSendingQuery = (state: ApplicationState): boolean => getPlaybookRoot(state).sendingQuery;

export const getUserPlaybookQueries = (state: ApplicationState): PlaybookQueryDB[] => getPlaybookRoot(state).queries;

export const getSuggestedChanges = (state: ApplicationState): SuggestedChanges | null => getPlaybookRoot(state).suggestedChanges;

export const getSuggestedChangesModalOpen = (state: ApplicationState): boolean => !isNull(getSuggestedChanges(state));

export const getAdditionalQuery = (state: ApplicationState): PlaybookMessage | null => getPlaybookRoot(state).additionalQuery;

export const getSuggestedChangesUpdated = (state: ApplicationState): boolean => getPlaybookRoot(state).suggestedChangesUpdated;

export const getConfirmationModalOpen = (state: ApplicationState): boolean => getPlaybookRoot(state).showConfirmationModal;

export const getSendingSuggestion = (state: ApplicationState): boolean => getPlaybookRoot(state).sendingSuggestion;

export const getUserPlaybookSuggestedChanges = (state: ApplicationState): SuggestedChangesDB[] => getPlaybookRoot(state).playbookSuggestedChanges;

export const getPlaybookSuggestedChange = (state: ApplicationState): SuggestedChangesDB | null => getPlaybookRoot(state).playbookSuggestedChange;

export const getReadOnlySuggestedChangesOpen = (state: ApplicationState): boolean => !isNull(getPlaybookSuggestedChange(state));

export const getOpenUserDeviations = (state: ApplicationState): string[] => getPlaybookRoot(state).openDeviations;

export const getSuggestedChangesMessage = (state: ApplicationState): PlaybookMessage | null => getPlaybookRoot(state).suggestedChangesMessage;

export const getSuggestedChangesMessageUpdated = (state: ApplicationState): boolean => getPlaybookRoot(state).suggestedChangesMessageUpdated;

export const getSendingSuggestedChangesMessage = (state: ApplicationState): boolean => getPlaybookRoot(state).sendingMessage;

export const getReadOnlyChangesContent = (state: ApplicationState): ReadOnlyContentType => getPlaybookRoot(state).readOnlySuggestedChangesContent;

export const getShowUserPlaybookLoadingSpinner = (state: ApplicationState): boolean => getPlaybookRoot(state).isLoading;

export const getPlaybookSimpleSearchTerm = (state: ApplicationState): string => getPlaybookRoot(state).simpleSearchTerm;

export const getPlaybookSmartSearchTerm = (state: ApplicationState): PlaybookSmartSearch[] => getPlaybookRoot(state).smartSearchTerm;

export const getProvisionLinksModalOpen = (state: ApplicationState): string | null => getPlaybookRoot(state).linkedProvisionModalOpen;

export const getLinkedProvisionHistory = (state: ApplicationState): LinkedPlaybookProvision[] => getPlaybookRoot(state).provisionLinkHistory;

export const getLinkedProvisionHistoryModalOpen = (state: ApplicationState): boolean => getPlaybookRoot(state).provisionLinkHistoryModalOpen;

